import { IRelationship } from '@innerhive-internal/innerhive-common';
import { IAcceptedCareMapInvite } from './ICareMapInvite';
import { ILink } from './WithLinks';
import { ISO8601 } from '../support/ISO8601';

export type { IRelationship };

export const SUPPORT_GROUP_KEYS = ['community', 'financial', 'school', 'medical', 'specialists', 'social'] as const;
export const KEYS_WITH_MEMBERS = [...SUPPORT_GROUP_KEYS, 'household'] as const;
export const KEYS_WITH_NOTES = [...KEYS_WITH_MEMBERS, 'patient'] as const;

export type ISupportGroupKey = typeof SUPPORT_GROUP_KEYS[number];
export type IKeyWithMembers = typeof KEYS_WITH_MEMBERS[number];
export type ICareMapSection = typeof KEYS_WITH_NOTES[number];

// Mirrors the API's MemberSourceSchema (caremaps/model.ts) — marks a care-network member as
// AI-extracted so it can be told apart from one the caregiver entered by hand.
export interface IMemberSource {
    type: 'manual' | 'ai-generated';
    noteId?: string;
    recordingId?: string;
}

export interface IFamilyMember {
    // for user / invite / member mappings
    /** @deprecated use contact-method email + linkedUserId */
    userEmail?: string;

    firstName?: string;
    lastName?: string;
    photo?: string;
    photoDataURI?: string;
    color?: string;
    avatar?: string;
    address?: string;
    contactMethods: IContactMethod[];
};

export interface IDiagnosis {
    id?: string;
    diagnosisId?: string;
    diagnosis?: string;
    status?: 'active' | 'resolved';
    diagnosedDate?: string;
    notes?: string;
};

export interface IAllergy {
    id?: string;
    allergyId?: string;
    allergy?: string;
    reaction?: string;
    severity?: 'mild' | 'moderate' | 'severe';
    notes?: string;
};

export const NEW_MEDICATION_ID = 'add-medication';
export const NEW_ALLERGY_ID = 'add-allergy';
export const NEW_DIAGNOSIS_ID = 'add-diagnosis';
export interface IMedication {
    id?: string;
    name: string;
    instructions?: string;
    dose?: string;
    rxNumber?: string;
    refills?: string;
    expiration?: string;
    notes?: string;
    noLongerTaking?: boolean;
};
export interface IPatient extends IFamilyMember {
    // the account this patient node is linked to once the person has accepted / is the owner-self.
    // presence flips the email field to read-only in the patient edit form.
    linkedUserId?: string;

    dob?: string;
    diagnoses?: IDiagnosis[];
    diagnosesIsPublic?: boolean;
    allergies?: IAllergy[];
    allergiesIsPublic?: boolean;
    medications?: IMedication[];
    medicationsIsPublic?: boolean;
    notesIsPublic?: boolean;
    links?: {
        notes: ILink;
        // Present when the API offered the authorised, audited route for this photo. Absent on a
        // member with no photo, and on anything served by a deploy older than that route.
        photo?: ILink;
    }
};

export interface IHouseholdMember extends IFamilyMember {
    id: string;
    relationshipId?: IRelationship;
    relationship?: string;
    primaryCaregiver?: boolean;
    linkedUserId?: string;
    source?: IMemberSource;
    links?: {
        notes: ILink;
        // Present when the API offered the authorised, audited route for this photo. Absent on a
        // member with no photo, and on anything served by a deploy older than that route.
        photo?: ILink;
    }
};

export type IContactMethodType = 'mobile-phone' | 'work-phone' | 'email';
export interface IContactMethod {
    type: IContactMethodType;
    value: string;
};

export interface ISupportGroupMember {
    id: string;
    linkedUserId?: string;
    name?: string;
    roleId?: string;
    role?: string;
    organization?: string;
    address?: string;
    contactMethods: IContactMethod[];
    source?: IMemberSource;
    links?: {
        notes: ILink;
        // Present when the API offered the authorised, audited route for this photo. Absent on a
        // member with no photo, and on anything served by a deploy older than that route.
        photo?: ILink;
    }
};

export interface IHousehold {
    members: IHouseholdMember[];
    checklists: IChecklist[];
    links?: {
        notes: ILink;
        // Present when the API offered the authorised, audited route for this photo. Absent on a
        // member with no photo, and on anything served by a deploy older than that route.
        photo?: ILink;
    }
};

export interface IChecklist {
    html: string;
    templateId?: string;
};

export interface ISupportGroup {
    members: ISupportGroupMember[];
    isRelevant: boolean | undefined;
    isRelevantSince?: string;
    checklists: IChecklist[];
    links?: {
        notes: ILink;
        // Present when the API offered the authorised, audited route for this photo. Absent on a
        // member with no photo, and on anything served by a deploy older than that route.
        photo?: ILink;
    }
};

export interface INewCareMap {
    links?: any;
    careMapId?: string;
    patient: IPatient;
    household: IHousehold;
    community: ISupportGroup;
    financial: ISupportGroup;
    school: ISupportGroup;
    medical: ISupportGroup;
    specialists: ISupportGroup;
    social: ISupportGroup;
    essentialMapComplete: boolean;
    initialWizardAnswers: {
        [questionId: string]: any;
    }
};

export type ICaremapAccess = 'co-owner' | 'view-only';
export interface ICareMap extends INewCareMap {
    userId: string;
    careMapId: string;
    version: number;
    household: IHousehold;
    // Owner's display name, included by the API only on single-caremap GETs for non-owners
    // (undefined for the owner's own caremaps and in list responses).
    ownerName?: string;
};

export interface ICareMapKey {
    careMapId: string;
    userId: string;
};

// TODO: ILightCareMap does NOT extend ICareMap (it is a lighter version)
export interface ILightCareMap extends ICareMap {
    orgShares: {
        userId: string;
        access: ICaremapAccess;
        invite: IAcceptedCareMapInvite;
        careMapKey: ICareMapKey;
        consentedAt: ISO8601;
        consentedBy: {
            email: string;
            userId: string;
        }
    }[];
};

export interface IWizardAnswer {
    questionId: string;
    answer: any;
};
